// locationService.js
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

/**
 * Get user's current location
 * @returns {Promise<{lat: number, lng: number}>}
 */
export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        console.warn('Could not get user location:', error);
        // Fallback to Orlando, FL coordinates if location access is denied
        resolve({
          lat: 28.5383,
          lng: -81.3792
        });
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000 // 5 minutes
      }
    );
  });
};

let googleMapsLoaderPromise = null;

/**
 * Load Google Maps JavaScript API if not already loaded.
 * Reuses an existing script tag when present to avoid double-loading and races.
 * @returns {Promise<void>}
 */
const loadGoogleMapsAPI = () => {
  if (window.google && window.google.maps) {
    return Promise.resolve();
  }

  if (googleMapsLoaderPromise) {
    return googleMapsLoaderPromise;
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector("script[src*='maps.googleapis.com/maps/api/js']");
    if (existingScript) {
      const waitForMaps = () => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }
        setTimeout(waitForMaps, 50);
      };

      existingScript.addEventListener("load", waitForMaps, { once: true });
      existingScript.addEventListener("error", () => {
        reject(new Error("Failed to load Google Maps API"));
      }, { once: true });

      waitForMaps();
      return;
    }

    const apiKey = GOOGLE_MAPS_API_KEY || "AIzaSyDjjjqcvAGEasl94fn7x2yEUM9M4QP06Mg";
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=geometry,places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google Maps API"));
    document.head.appendChild(script);
  }).catch((error) => {
    googleMapsLoaderPromise = null;
    throw error;
  });

  return googleMapsLoaderPromise;
};

/**
 * Geocode an address to get lat/lng coordinates
 * @param {string} address - The address to geocode
 * @returns {Promise<{lat: number, lng: number}>}
 */
const geocodeAddress = async (address) => {
  if (!address) {
    throw new Error('No address provided for geocoding');
  }

  await loadGoogleMapsAPI();

  return new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    
    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng()
        });
      } else {
        reject(new Error(`Geocoding failed: ${status}`));
      }
    });
  });
};

/**
 * Ensure all locations have lat/lng coordinates
 * @param {Array} locations - Array of location objects
 * @returns {Promise<Array>} Array of locations with coordinates
 */
const ensureCoordinates = async (locations) => {
  const promises = locations.map(async (location) => {
    // If location already has coordinates, return as is
    if (location.lat && location.lng && 
        typeof location.lat === 'number' && 
        typeof location.lng === 'number') {
      return location;
    }

    // Try to geocode the address if available
    if (location.address) {
      try {
        const coords = await geocodeAddress(location.address);
        console.log(`Geocoded ${location.name || location.id}: ${location.address} -> ${coords.lat}, ${coords.lng}`);
        return {
          ...location,
          lat: coords.lat,
          lng: coords.lng
        };
      } catch (error) {
        console.warn(`Failed to geocode ${location.name || location.id}: ${location.address}`, error);
        return location; // Return without coordinates
      }
    }

    return location; // Return without coordinates if no address
  });

  return Promise.all(promises);
};

/**
 * Calculate distance between two points using Google Maps Distance Matrix Service
 * @param {Object} origin - {lat, lng}
 * @param {Array} destinations - Array of {lat, lng, id, ...otherData}
 * @returns {Promise<Array>} Array of destinations with distance and duration added
 */
export const calculateDistances = async (origin, destinations) => {
  if (!destinations || destinations.length === 0) {
    return [];
  }

  try {
    // Load Google Maps API if not already loaded
    await loadGoogleMapsAPI();

    // Filter out destinations without valid coordinates
    const validDestinations = destinations.filter(dest => 
      dest.lat && dest.lng && 
      typeof dest.lat === 'number' && 
      typeof dest.lng === 'number'
    );

    if (validDestinations.length === 0) {
      console.warn('No valid destinations with coordinates found');
      return destinations.map(dest => ({
        ...dest,
        distance: { value: Infinity, text: 'Unknown' },
        duration: { value: Infinity, text: 'Unknown' },
        distanceValue: Infinity
      }));
    }

    // Use Google Maps Distance Matrix Service
    const service = new window.google.maps.DistanceMatrixService();
    const originLatLng = new window.google.maps.LatLng(origin.lat, origin.lng);
    const destinationLatLngs = validDestinations.map(dest => 
      new window.google.maps.LatLng(dest.lat, dest.lng)
    );

    return new Promise((resolve) => {
      service.getDistanceMatrix({
        origins: [originLatLng],
        destinations: destinationLatLngs,
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.IMPERIAL,
        avoidHighways: false,
        avoidTolls: false
      }, (response, status) => {
        if (status === window.google.maps.DistanceMatrixStatus.OK && response.rows[0]) {
          const results = validDestinations.map((dest, index) => {
            const element = response.rows[0].elements[index];
            
            if (element.status === 'OK') {
              return {
                ...dest,
                distance: element.distance,
                duration: element.duration,
                distanceValue: element.distance.value // for sorting
              };
            } else {
              return {
                ...dest,
                distance: { value: Infinity, text: 'Unknown' },
                duration: { value: Infinity, text: 'Unknown' },
                distanceValue: Infinity
              };
            }
          });

          // Add back any destinations that didn't have valid coordinates
          const invalidDestinations = destinations
            .filter(dest => !dest.lat || !dest.lng || typeof dest.lat !== 'number' || typeof dest.lng !== 'number')
            .map(dest => ({
              ...dest,
              distance: { value: Infinity, text: 'Unknown' },
              duration: { value: Infinity, text: 'Unknown' },
              distanceValue: Infinity
            }));

          resolve([...results, ...invalidDestinations]);
        } else {
          console.error('Distance Matrix service failed:', status);
          // Return original destinations with unknown distances if API fails
          resolve(destinations.map(dest => ({
            ...dest,
            distance: { value: Infinity, text: 'Unknown' },
            duration: { value: Infinity, text: 'Unknown' },
            distanceValue: Infinity
          })));
        }
      });
    });

  } catch (error) {
    console.error('Error calculating distances:', error);
    // Return original destinations with unknown distances if API fails
    return destinations.map(dest => ({
      ...dest,
      distance: { value: Infinity, text: 'Unknown' },
      duration: { value: Infinity, text: 'Unknown' },
      distanceValue: Infinity
    }));
  }
};

/**
 * Sort locations by distance from user
 * @param {Array} locations - Array of location objects with distance data
 * @returns {Array} Sorted array (closest first)
 */
export const sortLocationsByDistance = (locations) => {
  return [...locations].sort((a, b) => {
    const aDistance = a.distanceValue || a.distance?.value || Infinity;
    const bDistance = b.distanceValue || b.distance?.value || Infinity;
    return aDistance - bDistance;
  });
};

/**
 * Filter locations by active status
 * @param {Array} locations - Array of location objects
 * @returns {Array} Filtered array of active locations
 */
export const filterActiveLocations = (locations) => {
  return locations.filter(location => {
    // If active field doesn't exist, default to true
    return location.active !== false;
  });
};

/**
 * Process locations: filter active ones, ensure coordinates, calculate distances, and sort
 * @param {Array} locations - Array of location objects from Firebase
 * @param {Object} userLocation - {lat, lng} of user's position
 * @returns {Promise<Array>} Processed and sorted locations
 */
export const processLocations = async (locations, userLocation) => {
  try {
    // Filter by active status first
    const activeLocations = filterActiveLocations(locations);
    console.log(`Filtered ${activeLocations.length} active locations out of ${locations.length} total locations`);

    if (activeLocations.length === 0) {
      return [];
    }

    // Ensure all locations have coordinates
    const locationsWithCoords = await ensureCoordinates(activeLocations);

    // Calculate distances if user location is available
    if (userLocation) {
      const locationsWithDistances = await calculateDistances(userLocation, locationsWithCoords);
      return sortLocationsByDistance(locationsWithDistances);
    }

    return locationsWithCoords;
  } catch (error) {
    console.error('Error processing locations:', error);
    return filterActiveLocations(locations); // Return at least the filtered locations
  }
};
